import Bluebird from "bluebird";
import { sortBy, sortedIndexBy, uniqBy } from "lodash";

import { GraphQLAPI, GraphQLResult, queries } from "@examspark/api";
import {
  doesHierarchyTypeInclude,
  flattenObjectivesTree,
  getObjectiveHierarchyType,
  itemKey,
} from "@examspark/common";
import {
  IDSK,
  Objective,
  ObjectiveHierarchyType,
  ObjectiveTree,
} from "@examspark/types";

import { createObjectivePathKeys } from "./objectives";

export async function getObjectivesTree<T extends Objective>(
  objectives: T[],
  rootType?: ObjectiveHierarchyType,
): Promise<Array<ObjectiveTree<T>>>;
export async function getObjectivesTree<T extends Objective>(
  objectives: T[] | undefined,
  rootType?: ObjectiveHierarchyType,
): Promise<Array<ObjectiveTree<T>> | undefined>;
export async function getObjectivesTree<T extends Objective>(
  objectives: T[] | undefined,
  rootType: ObjectiveHierarchyType = "authority",
): Promise<Array<ObjectiveTree<T>> | undefined> {
  if (objectives == null) return;

  const { keys, hierarchy } =
    (await getHierarchyScopesKeyed(
      objectives.map((o) => o.scope ?? undefined),
      rootType,
    )) ?? {};

  for (let i = 0; i < objectives.length; i++) {
    connectParentToChild(hierarchy, keys[i].sectionKey, objectives[i]);
  }

  return getRoots(hierarchy, rootType);
}

export const getHierarchyScopesKeyed = async (
  sectionScopes: Array<string | undefined>,
  rootType: ObjectiveHierarchyType = "authority",
) => {
  const keys = sectionScopes.map((scope) =>
    createObjectivePathKeys(scope ?? undefined),
  );

  const uniqueKeys: IDSK[] = [
    ...getKeysByTypeIfIncluded(keys, rootType, "authority"),
    ...getKeysByTypeIfIncluded(keys, rootType, "publication"),
    ...getKeysByTypeIfIncluded(keys, rootType, "document"),
    ...getKeysByTypeIfIncluded(keys, rootType, "section"),
  ];

  const hierarchy: Record<string, ObjectiveTree<Objective>> = {};

  await Bluebird.map(
    uniqueKeys,
    async (key) => {
      const result = (await GraphQLAPI.client.query({
        query: queries.objective.getObjective,
        variables: {
          id: key.id,
          sk: key.sk,
        },
      })) as GraphQLResult<{
        getObjective: Objective;
      }>;

      if (result.data == null) return;

      hierarchy[itemKey(result.data.getObjective)] = {
        objective: result.data.getObjective,
        children: [],
      };
    },
    { concurrency: 50 },
  );

  for (const key of keys) {
    connectParentToChild(hierarchy, key.authorityKey, key.publicationKey);
    connectParentToChild(hierarchy, key.publicationKey, key.documentKey);
    connectParentToChild(hierarchy, key.documentKey, key.sectionKey);
  }

  return { keys, hierarchy };
};

export const sortObjectivesByTree = async <T extends Objective>(
  objectives: T[],
  rootType: ObjectiveHierarchyType = "authority",
) => {
  const tree = await getObjectivesTree(objectives, rootType);
  return flattenObjectivesTree(tree, rootType);
};

export async function sortSectionsScopesByTree(
  sectionScopes: Array<string | undefined>,
  rootType?: ObjectiveHierarchyType,
): Promise<Objective[]>;
export async function sortSectionsScopesByTree(
  sectionScopes: Array<string | undefined> | undefined,
  rootType?: ObjectiveHierarchyType,
): Promise<Objective[] | undefined>;
export async function sortSectionsScopesByTree(
  sectionScopes: Array<string | undefined> | undefined,
  rootType?: ObjectiveHierarchyType,
): Promise<Objective[] | undefined>;
export async function sortSectionsScopesByTree(
  sectionScopes: Array<string | undefined> | undefined,
  rootType: ObjectiveHierarchyType = "authority",
) {
  if (sectionScopes === undefined) return undefined;

  const { hierarchy } = await getHierarchyScopesKeyed(sectionScopes, rootType);
  const roots = getRoots(hierarchy, rootType);
  return flattenObjectivesTree(roots, rootType, "section");
}

const connectParentToChild = <T extends Objective>(
  results: Record<string, ObjectiveTree<T>>,
  parentKey: IDSK,
  childOrChildKey: IDSK | Objective,
) => {
  const parent = results[itemKey(parentKey)];
  if (parent == null) return;
  if (parent.children == null) parent.children = [];

  let child: ObjectiveTree<T>;
  if ("scope" in childOrChildKey) {
    child = { objective: childOrChildKey, children: [] };
  } else {
    child = results[itemKey(childOrChildKey)];
  }

  if (parent.children.includes(child)) return;

  const index = sortedIndexBy(
    parent.children,
    child,
    (i) => i.objective.sequence ?? i.objective.sk,
  );

  parent.children.splice(index, 0, child);
};

const getKeysByTypeIfIncluded = (
  keys: Array<ReturnType<typeof createObjectivePathKeys>>,
  rootType: ObjectiveHierarchyType,
  type: ObjectiveHierarchyType,
) => {
  if (!doesHierarchyTypeInclude(rootType, type)) return [];
  return uniqBy(
    keys.map((k) => k[`${type}Key`]),
    (k) => (type === "authority" ? k.id : k.sk),
  );
};

const getRoots = (
  hierarchy: Record<string, ObjectiveTree<Objective>>,
  rootType: ObjectiveHierarchyType = "authority",
) =>
  sortBy(
    Object.values(hierarchy).filter(
      (r) => getObjectiveHierarchyType(r.objective) === rootType,
    ),
    (r) => r.objective.sequence ?? r.objective.content,
  );
